import Papa from 'papaparse';
import { readSheet } from 'read-excel-file/browser';

import { BulkCategoryRow } from '~/services/ExperiencesService';

// Lines whose first cell starts with this prefix are reference/comment lines (e.g. the valid
// category list in the generated template) and are ignored when reading upload rows.
export const CSV_COMMENT_PREFIX = '#';

export type ParseResult = {
  rows: BulkCategoryRow[];
  errors: Array<{ row: number; message: string }>;
};

function parseCategoryTags(raw: string): string[] {
  if (!raw) return [];
  // Support both `"Cat 1","Cat 2"` and plain comma-separated `Cat 1,Cat 2`
  const quoted = raw.match(/"([^"]+)"/g);
  if (quoted) {
    return quoted.map((s) => s.replace(/"/g, '').trim()).filter(Boolean);
  }
  return raw
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

function isCommentRow(row: unknown[]): boolean {
  return String(row[0] ?? '')
    .trim()
    .startsWith(CSV_COMMENT_PREFIX);
}

function rowsFromCells(cells: unknown[][]): Array<Record<string, string>> {
  const [headers = [], ...rows] = cells;
  const headerNames = headers.map((header) => String(header ?? '').trim());

  return rows
    .filter((row) => !isCommentRow(row))
    .map((row) =>
      headerNames.reduce<Record<string, string>>((record, header, index) => {
        record[header] = String(row[index] ?? '');
        return record;
      }, {}),
    );
}

async function readRows(file: File): Promise<Array<Record<string, string>>> {
  if (file.name.toLowerCase().endsWith('.csv')) {
    const text = await file.text();
    return Papa.parse<Record<string, string>>(text, {
      header: true,
      skipEmptyLines: true,
      comments: CSV_COMMENT_PREFIX,
    }).data;
  }

  const cells = (await readSheet(file)) as unknown[][];
  return rowsFromCells(cells);
}

export async function parseExperienceCategoryFile(file: File): Promise<ParseResult> {
  try {
    const rawRows = await readRows(file);
    const rows: BulkCategoryRow[] = [];
    const errors: Array<{ row: number; message: string }> = [];

    rawRows.forEach((raw, idx) => {
      const rowNum = idx + 2; // 1-indexed + header row
      const offerId = (raw['DEAL ID'] ?? '').toString().trim();
      const categoryTagsRaw = (raw['CATEGORY TAGS'] ?? '').toString().trim();
      const action = (raw['ACTION'] ?? '').toString().trim().toLowerCase();

      if (!offerId) {
        errors.push({ row: rowNum, message: 'Missing DEAL ID' });
        return;
      }

      if (action !== 'add' && action !== 'remove') {
        errors.push({ row: rowNum, message: `Invalid ACTION "${action}" — must be "add" or "remove"` });
        return;
      }

      const categories = parseCategoryTags(categoryTagsRaw);
      if (categories.length === 0) {
        errors.push({ row: rowNum, message: 'At least one CATEGORY TAG is required' });
        return;
      }

      rows.push({ offerId, categories, action: action as 'add' | 'remove' });
    });

    return { rows, errors };
  } catch (err) {
    throw new Error(`Failed to parse file: ${err instanceof Error ? err.message : String(err)}`, { cause: err });
  }
}
